/**
 * En este archivo vamos a definir toda logica de filtros. Esto NO es un singleton, por lo que lo que
 * se haga aquí debe ser implementado, esto es solo una definicion de una clase.
 * */
import {AcopioViewModel, ClienteViewModel, UserViewModel} from '@utils/ViewModels';
import * as moment from 'moment';
import {isArrayLike} from 'rxjs/util/isArrayLike';

export enum FilterType {
  Fecha,
  FechaFromTo,
  Select,
  Cliente,
  Number,
  NumberFromTo,
  Colaborador,
  Acopio,
  Autorizante,
}

export type NegociosFiltersAndSort = {
  filters: NegociosFilter;
  sort: {
    column: any;
    order: boolean;
  }
  quickFilters: IQuickFilters;
  pageSize: number;
  currentPage: number;
}

export class NegociosFilter {
  filtersBackup: GenericFilter[] = [];
  filters: GenericFilter[] = [];
  constructor(private filterArray?: GenericFilter[]) {
    if (filterArray) {
      this.buildFilter(filterArray);
    }
  }

  public buildFilter(filterArray: GenericFilter[]) {
    this.filtersBackup = filterArray.map(x => x);
    this.filters = filterArray.map(x => x);
  }

  public getFilter(id: string): GenericFilter | null {
    return this.filters.find(x => x.name === id) || {name: '', type: 0, data: null};
  }

  public deleteFilter(id: string) {
    const i = this.filters.findIndex(x => x.name === id);
    if (i >= 0) {
      this.filters[i].data = null;
    }
  }

  public resetFilter() {
    this.filters = this.filtersBackup.map(x => x);
  }

  setSingleFilter(filterId: string, filter: GenFilterTypes) {
    const foundIndex = this.filters.findIndex(x => x.name === filterId);
    if (foundIndex === -1) { return false; }
    this.filters[foundIndex].data = filter;
    return true;
  }

  public getChipList(): ChipDisplay[] {
    return this.filters.filter(x => x.data !== null).map(x => this.getChip(x));
  }

  private getChip(filter: GenericFilter): ChipDisplay {
    switch (filter.type) {
      case FilterType.Fecha:
        return {
          Id: filter.name,
          ChipValue: `${filter.name} / ${moment(filter.data as Date).format('DD/MM/YY')}`
        };
      case FilterType.FechaFromTo:
        const data = filter.data as GenFilterFechaFromTo;
        return {
          Id: filter.name,
          ChipValue: `${filter.name} / ${moment(data.from).format('DD/MM/YY')} - ${moment(data.to).format('DD/MM/YY')}`
        };
      case FilterType.Select:
        if (isArrayLike(filter.data)) {
          const arr = filter.data as GenFilterSelect[];
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${arr.length > 1 ? 'Varios' : arr[0].displayValue}`
          };
        } else {
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${(filter.data as GenFilterSelect).displayValue}`
          };
        }
      case FilterType.Acopio:
        if (isArrayLike(filter.data)) {
          const arr = filter.data as GenFilterAcopio[];
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${arr.length > 1 ? 'Varios' : arr[0].displayValue}`
          };
        } else {
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${(filter.data as GenFilterAcopio).displayValue}`
          };
        }
      case FilterType.Cliente:
        if (isArrayLike(filter.data)) {
          const arr = filter.data as GenFilterCliente[];
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${arr.length > 1 ? 'Varios' : arr[0].displayValue}`
          };
        } else {
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${(filter.data as GenFilterCliente).displayValue}`
          };
        }
      case FilterType.Colaborador:
        if (isArrayLike(filter.data)) {
          const arr = filter.data as GenFilterColaborador[];
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${arr.length > 1 ? 'Varios' : arr[0].displayValue}`
          };
        } else {
          return {
            Id: filter.name,
            ChipValue: `${filter.name} / ${(filter.data as GenFilterColaborador).displayValue}`
          };
        }
      case FilterType.Number:
        return {
          Id: filter.name,
          ChipValue: `${filter.name} / ${((filter.data) as number)}`
        };
      case FilterType.NumberFromTo:
        const dataNum = filter.data as GenFilterNumberFromTo;
        return {
          Id: filter.name,
          ChipValue: `${filter.name} / ${dataNum.From} - ${dataNum.To}`
        };
      case FilterType.Autorizante:
        const dataAuto = filter.data as GenFilterAutorizante[];
        return {
          Id: filter.name,
          ChipValue: `${filter.name} / ${dataAuto.length > 1 ? 'Varios' : dataAuto[0].displayValue}`
        };
      default:
        return {Id: '', ChipValue: 'ChipInvalido'};
    }
  }

}

export type GenFilterTypes =  GenFilterFechaFromTo |
  GenFilterSelect |
  GenFilterSelect[] |
  GenFilterCliente |
  GenFilterCliente[] |
  GenFilterNumberFromTo |
  GenFilterColaborador |
  GenFilterColaborador[] |
  GenFilterAcopio |
  GenFilterAcopio[] |
  GenFilterAutorizante[] |
  number | string | boolean | Date | null;


export interface GenericFilter {
  name: string;
  type: FilterType;
  data: GenFilterTypes;
}

// Filtro generico de Fecha. Acepta tipo de dato en caso de querer usar Unix o Date.
export interface GenFilterFechaFromTo {
  from: Date;
  to: Date;
}
// Select. Acepta tipos genericos, con un displayValue y un Id para facil acceso a los identificadores
export interface GenFilterSelect {
  displayValue?: string;
  Id?: number | string;
  rawValue: string;
}
export interface GenFilterCliente {
  displayValue?: string;
  Id?: number | string;
  data: ClienteViewModel;
}

export interface GenFilterNumberFromTo {
  From: number;
  To: number;
}

export interface GenFilterColaborador {
  displayValue?: string;
  Id?: number | string;
  data: UserViewModel;
}

export interface GenFilterAutorizante {
  displayValue?: string;
  Id?: number | string;
  data: UserViewModel;
}

export interface GenFilterAcopio {
  displayValue?: string;
  Id?: number | string;
  data: AcopioViewModel;
}

export interface ChipDisplay {
  Id: string | number;
  ChipValue: string;
}

export const NC = {
  actualizar: {
    name: 'Actualizar',
    width: 80,
    filterOrderName: '',
  },
  hora: {
    name: 'Marca Temporal',
    width: 150,
    filterOrderName: 'HoraConfirmacion',
  },
  confirmacion: {
    name: 'Estado',
    width: 105,
    filterOrderName: 'Confirmacion',
  },
  fechaEstado: {
    name: 'Fecha Estado',
    width: 120,
    filterOrderName: 'FechaEstado',
  },
  tipo: {
    name: 'Tipo',
    width: 75,
    filterOrderName: 'Tipo',
  },
  cliente: {
    name: 'Cliente',
    width: 110,
    filterOrderName: 'Cliente',
  },
  producto: {
    name: 'Producto',
    width: 100,
    filterOrderName: 'Producto',
  },
  tns: {
    name: 'Tns',
    width: 70,
    filterOrderName: 'Toneladas',
  },
  negocio: {
    name: 'Negocio',
    width: 100,
    filterOrderName: 'Negocio',
  },
  entrega: {
    name: 'Entrega',
    width: 100,
    filterOrderName: 'Entrega',
  },
  cosecha: {
    name: 'Cosecha',
    width: 100,
    filterOrderName: 'Cosecha',
  },
  moneda: {
    name: 'Moneda',
    width: 100,
    filterOrderName: 'Moneda',
  },
  base: {
    name: 'Base',
    width: 95,
    filterOrderName: 'Base',
  },
  precio: {
    name: 'Precio / Idea',
    width: 115,
    filterOrderName: 'PrecioIdea',
  },
  precioEspecial: {
    name: 'Precio Especial',
    width: 130,
    filterOrderName: 'PrecioEspecial',
  },
  match: {
    name: 'Match',
    width: 75,
    filterOrderName: 'Match',
  },
  autorizante: {
    name: 'Autorizante',
    width: 100,
    filterOrderName: 'Autorizante',
  },
  responsable: {
    name: 'Responsable',
    width: 110,
    filterOrderName: 'Responsable',
  },
  acopio: {
    name: 'Acopio',
    width: 100,
    filterOrderName: 'Acopio',
  },
  zona: {
    name: 'Zona',
    width: 110,
    filterOrderName: 'Zona',
  },
  vencimiento: {
    name: 'Vencimiento',
    width: 110,
    filterOrderName: 'Vencimiento',
  },
  creacion: {
    name: 'Creación',
    width: 110,
    filterOrderName: 'Fecha',
  },
  mercado: {
    name: 'Mercado',
    width: 110,
    filterOrderName: 'Mercado',
  },
  fuente: {
    name: 'Fuente',
    width: 110,
    filterOrderName: 'Fuente',
  },
};

export interface IQuickFilters {
  Hoy: boolean;
  Ordenes: boolean;
  Intenciones: boolean;
  Matches: boolean;
  Confirmadas: boolean;
  Vencidas: boolean;
}



// WEBPACK FOOTER //
// E:/jenkins_home/workspace/_agenda-unco-front-legacy_master/frontend-web/src/app/components/negocios/negocios/negocio-filter.ts